<script setup>

import { ref } from 'vue';
import { useSubscriptionsStore } from '@/stores/subscriptions';
import { usePlansStore } from '@/stores/plans';
import { storeToRefs } from 'pinia';

const showCancelModal = ref(false);
const subscriptionToCancel = ref(null);

const subscriptionsStore = useSubscriptionsStore();
const { allSubscriptions } = storeToRefs(subscriptionsStore);

const plansStore = usePlansStore();
const { availablePlans } = storeToRefs(plansStore);

const subscribeToPlan = (planId) => {
    subscriptionsStore.subscribeToPlan({ plan_id: planId });
};

const resubscribe = (subscriptionId) => {
    subscriptionsStore.resubscribeToPlan({ subscription_id: subscriptionId });
};

const openCancelModal = (sub) => {
    subscriptionToCancel.value = sub;
    showCancelModal.value = true;
};

const confirmCancelSubscription = () => {
    if (subscriptionToCancel.value) {
        subscriptionsStore.unsubscribeFromPlan({ subscription_id: subscriptionToCancel.value.id });
    }
    showCancelModal.value = false;
    subscriptionToCancel.value = null;
};

const closeCancelModal = () => {
    showCancelModal.value = false;
    subscriptionToCancel.value = null;
};
</script>

<template>
<div>
    <h2 class="mb-4 text-2xl font-semibold">{{ $t('label.subscription') }}</h2>
    <div v-if="allSubscriptions && allSubscriptions.length">
        <div v-for="sub in allSubscriptions" :key="sub.id" class="mb-4 p-4 rounded-lg bg-white border border-blue-100 shadow-md">
            <div class="flex items-center justify-between mb-2">
                <span class="text-xl font-bold custom-tab-btn-text">{{ sub.plan?.name }}</span>
                <span 
                    :class="['px-2 py-1 rounded text-sm font-semibold', sub.global_status?.slug === 'active' ? 'bg-green-100 text-green-700' : sub.global_status?.slug === 'canceled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700']"
                    style="font-size: 1rem;">
                    {{ sub.global_status?.name ?? sub.stripe_status }}
                </span>
            </div>
            <div class="mb-2 flex items-center justify-between">
                <span class="text-base text-gray-500">{{ $t('label.renewal_date') }}: <span class="font-semibold text-gray-800">{{ sub.renewal_at ? new Date(sub.renewal_at).toLocaleDateString() : '-' }}</span></span>
                <span v-if="sub.canceled_at && sub.ends_at" class="text-base text-gray-500">{{ $t('label.ends_at') }}: <span class="font-semibold text-gray-800">{{ new Date(sub.ends_at).toLocaleDateString() }}</span></span>
            </div>
            <div class="flex items-center justify-between text-base text-gray-600" style="font-size: 1.125rem;">
                <span>{{ $t('label.price') }}: <span class="font-semibold text-gray-800">{{ sub.plan?.price }} {{ sub.plan?.currency }}</span> {{ $t('label.taxes_not_included') }}</span>
                <div class="flex items-center">
                    <span>{{ $t('label.quantity') }}: <span class="font-semibold text-gray-800">{{ sub.quantity }}</span></span>
                    <button 
                        v-if="!sub.canceled_at" 
                        class="ml-2 px-1 py-0.5 bg-red-300 text-white rounded text-sm font-semibold hover:bg-red-500 transition"
                        @click="openCancelModal(sub)">{{ $t('label.cancel') }}</button>
                    <button 
                        v-else 
                        class="ml-2 px-2 py-0.5 btn text-white rounded text-base font-semibold hover:bg-green-900 transition" 
                        @click="resubscribe({ subscription_id: sub.id })">{{ $t('label.resubscribe') }}</button>
                </div>
            </div>
            <!-- Cancel Subscription Confirmation Modal -->
            <Transition name="modal">
                <div v-if="showCancelModal" class="fixed inset-0 z-50 flex items-center justify-center bg-opacity-1">
                    <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                        <h3 class="mb-4 text-lg font-semibold text-gray-800">{{ $t('label.confirm_removal', { name: $t('label.subscription') }) }}</h3>
                        <p class="mb-6 text-gray-600">{{ $t('label.confirm_cancel_subscription_message') || 'Are you sure you want to cancel this subscription?' }}</p>
                        <div class="flex justify-end gap-2">
                            <button class="border px-4 py-2 rounded" @click="closeCancelModal">{{ $t('label.cancel') }}</button>
                            <button class="bg-red-500 text-white px-4 py-2 rounded" @click="confirmCancelSubscription">{{ $t('label.confirm') }}</button>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
            
    </div>
    <div>
        <div v-if="availablePlans && availablePlans.length">
            <h2 class="mb-4 text-2xl font-semibold">{{ $t('label.available_plans') }}:</h2>
            <div 
                v-for="plan in availablePlans" 
                :key="plan.id" 
                class="mb-4 p-4 rounded-lg shadow-md bg-white border border-blue-100  min-h-[120px]">
                <p><strong>{{ plan.name }}</strong> - {{ plan.price }} {{ plan.currency }}</p>
                <p>{{ plan.features }}</p>
                <!-- Show Subscribe Now button only if not schedule_call and not already subscribed -->
                <button
                    v-if="!plan.schedule_call && !allSubscriptions.some(sub => sub.plan?.id === plan.id && sub.is_current)"
                    class="mt-2 px-4 py-2 btn text-white rounded"
                    @click="subscribeToPlan(plan.id)">
                    {{ $t('label.subscribe_now') }}
                </button>
            </div>
        </div>
        <div v-else>
            {{ $t('label.no_plans_available') }}
        </div>
    </div>
</div>    
</template>

<style scoped>
    .custom-tab-btn {
        background: #00594C !important;
    }
    .custom-tab-btn-text {
        color: #00594C !important;
        border-color: #00594C !important;
    }
</style>
